import React, { JSX } from 'react'
import { AnalyticsMetafield } from 'types/api'
import { cleanVariables, formatPrice, graphqlRequest, numberFormatter } from 'lib/helpers'
import { EmptyDataCard } from 'components/EmptyDataCard'
import { RefreshingBlock } from './Loading'
import getTotals from '../graphql/getTotals.graphql'
import { useQuery } from '@tanstack/react-query'

export interface TotalsSectionProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const TotalsSection = ({ token, client, filters }: TotalsSectionProps): JSX.Element => {
  const {
    data,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['dashboard', 'getTotals', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate
      })

      const { overall } = await graphqlRequest<{ overall: { quantity: number, sales: number } }>(
        getTotals, cleanVar, token
      )
      return overall
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const withData = (
    <dl className='mx-4 md:mx-0 grid grid-cols-2 divide-y divide-lm-bg-main dark:divide-dm-bg-main md:grid-cols-2 md:divide-x md:divide-y-0'>
      <div key='total-sales' className='px-2 py-5 sm:p-6 text-left sm:text-center'>
        <dd className='mt-1 flex justify-between items-center gap-4'>
          <div className='flex-1'>
            <h3 className='text-l sm:text-xl'>Total Sales</h3>
            <p data-testid='totals-total-sales' className='text-3xl sm:text-4xl font-semibold text-brand'>
              {formatPrice(data?.sales ?? 0)}
            </p>
          </div>

        </dd>
      </div>
      <div key='total-quantity' className='px-2 py-5 sm:p-6 text-right sm:text-center '>
        <dd className='mt-1 flex justify-between items-center gap-4'>
          <div className='flex-1'>
            <h3 className='text-l sm:text-xl'>Total Quantity</h3>
            <p data-testid='totals-total-quantity' className='text-3xl sm:text-4xl font-semibold text-brand'>
              {numberFormatter(data?.quantity ?? 0)}
            </p>
          </div>
        </dd>
      </div>
    </dl>
  )

  const noData = <EmptyDataCard />

  return (
    <div className='relative mt-4 md:mt-0 overflow-hidden rounded-none md:rounded-2xl shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3
'
    >
      {isFetching && RefreshingBlock}
      {(data !== null || isError) ? withData : noData}
    </div>
  )
}
